import type {
  AffiliateDto,
  AppointmentDto,
  AnalyticsDto,
  AutomationSettingsInput,
  BroadcastDto,
  BroadcastSegment,
  CommissionDto,
  OrderAction,
  OrderDto,
  OrderSummaryDto,
  ProductDto,
  PromotionDto,
  ModifierGroupDto,
  ResourceDto,
  SlotDayDto,
  UpsertAffiliateInput,
  UpsertFnbItemInput,
  UpsertModifierGroupInput,
  UpsertResourceInput,
  UpsertServiceInput,
  UpsertBroadcastInput,
  PolicyStatusDto,
  ProhibitedCategory,
  ReturnPolicyTemplate,
  UpsertProductInput,
  UpsertPromotionInput,
} from '@telestore/contracts';

/**
 * Admin API client.
 *
 * Every call carries the session token and the shop id, so components never
 * thread either through their props. The token lives in memory plus
 * sessionStorage — never localStorage, because an admin session grants
 * refunds and bot access.
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
const TOKEN_KEY = 'telestore.admin.token';
const SHOP_KEY = 'telestore.admin.shop';

let token: string | null = null;
let shopId: string | null = null;
let locale: 'km' | 'en' = 'km';

export function restoreSession() {
  try {
    token = sessionStorage.getItem(TOKEN_KEY);
    shopId = sessionStorage.getItem(SHOP_KEY);
  } catch {
    // Private mode — the merchant signs in again, which is acceptable.
  }
  return { token, shopId };
}

export function setToken(next: string | null) {
  token = next;
  try {
    if (next) sessionStorage.setItem(TOKEN_KEY, next);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* non-fatal */
  }
}

export function setShopId(next: string | null) {
  shopId = next;
  try {
    if (next) sessionStorage.setItem(SHOP_KEY, next);
    else sessionStorage.removeItem(SHOP_KEY);
  } catch {
    /* non-fatal */
  }
}

export function getShopId(): string | null {
  return shopId;
}

export function setApiLocale(next: 'km' | 'en') {
  locale = next;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      'x-locale': locale,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(shopId ? { 'x-shop-id': shopId } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let code = 'internal';
    let message = `${response.status}`;
    let details: Record<string, unknown> | undefined;
    try {
      const body = (await response.json()) as {
        error?: { code: string; message: string; details?: Record<string, unknown> };
      };
      if (body.error) {
        code = body.error.code;
        message = body.error.message;
        details = body.error.details;
      }
    } catch {
      /* keep the status text */
    }
    if (response.status === 401) setToken(null);
    throw new ApiError(code, message, details, response.status);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await response.text()) as T;
  return (await response.json()) as T;
}

/* ------------------------------------------------------------------ */
/* Response shapes                                                     */
/* ------------------------------------------------------------------ */

export interface SessionPayload {
  user: {
    id: string;
    telegramUserId: number;
    firstName: string;
    lastName: string | null;
    username: string | null;
    photoUrl: string | null;
    locale: 'km' | 'en';
  };
  memberships: Array<{ shopId: string; shopName: string; shopSlug: string; role: 'owner' | 'staff' }>;
  token: string;
  expiresAt: string;
}

/** Mirrors `OnboardingStepId` in the api; the order is the dependency order. */
export type OnboardingStepId =
  | 'accept_policies'
  | 'add_product'
  | 'choose_theme'
  | 'connect_bot'
  | 'payment_details';

export interface ShopPayload {
  shop: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    currency: 'USD' | 'KHR';
    defaultLocale: 'km' | 'en';
    /** §4: decides which of the vertical screens the nav shows at all. */
    vertical: 'retail' | 'fnb' | 'services' | 'digital';
    plan: 'free' | 'starter' | 'pro' | 'business';
    status: 'active' | 'suspended' | 'closed';
    theme: { template: string; overrides?: Record<string, string>; logo: string | null; cover: string | null };
    settings: {
      contact_telegram_username: string | null;
      contact_phone: string | null;
      payment_window_minutes: number;
      cod_enabled: boolean;
      prepaid_enabled: boolean;
      auto_confirm_orders: boolean;
      low_stock_threshold: number;
      return_policy: { km: string; en: string };
      khqr: {
        bakong_account_id: string;
        /** Tag 29 sub-tag 01 — the field that routes the money (§3). */
        account_number?: string;
        bank_name?: string;
        merchant_name: string;
        merchant_city: string;
        account_label?: string;
      } | null;
    };
  };
  role: 'owner' | 'staff';
  bot: {
    id: string;
    username: string | null;
    state: 'pending' | 'active' | 'revoked' | 'error';
    provisioning: 'managed' | 'manual';
    b2bMode: boolean;
    privacyModeOff: boolean;
    abaGroupChatId: number | null;
    abaGroupTitle: string | null;
    abaListenerState: 'off' | 'awaiting_group' | 'missing_admin' | 'active' | 'error';
    lastError: string | null;
  };
  subscription: { plan: string; status: string; periodEnd: string | null } | null;
  credits: { plan: number; purchased: number; total: number; available: number };
  templates: Array<{ id: string; name: string; premium: boolean; blurb: { km: string; en: string }; tokens: Record<string, string> }>;
  storefrontUrl: string;
}

export interface DashboardPayload {
  stats: {
    pendingConfirm: number;
    awaitingPayment: number;
    ordersToday: number;
    revenueToday: number;
  };
  currency: 'USD' | 'KHR';
  lowStock: Array<{
    productId: string;
    name: { km: string; en: string };
    variantId: string;
    size: string | null;
    color: string | null;
    stock: number;
  }>;
  unmatchedPayments: Array<{
    id: string;
    amount: number | null;
    currency: 'USD' | 'KHR' | null;
    payerName: string | null;
    occurredAt: string | null;
  }>;
  credits: { total: number; available: number };
}

export interface PlatformCharge {
  id: string;
  kind: 'subscription' | 'credit_pack';
  ref: string;
  status: string;
  currency: 'USD' | 'KHR';
  amount: number;
  chargeAmount: number;
  qrPayload: string | null;
  expiresAt: string | null;
}

export const api = {
  /* ------------------------------------------------------------------ */
  /* §4 verticals                                                         */
  /*                                                                      */
  /* Deliberately not entitlement-gated. A vertical is what a shop IS, not */
  /* a feature it buys — a coffee shop on the Starter plan still needs its */
  /* modifier groups, or it cannot sell a coffee at all.                   */
  /* ------------------------------------------------------------------ */

  /* --- F&B ---------------------------------------------------------- */

  modifierGroups: (shop: string) =>
    request<{ groups: ModifierGroupDto[] }>(`/admin/shops/${shop}/modifier-groups`),

  createModifierGroup: (shop: string, body: UpsertModifierGroupInput) =>
    request<{ group: ModifierGroupDto }>(`/admin/shops/${shop}/modifier-groups`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateModifierGroup: (shop: string, groupId: string, body: UpsertModifierGroupInput) =>
    request<{ group: ModifierGroupDto }>(`/admin/shops/${shop}/modifier-groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteModifierGroup: (shop: string, groupId: string) =>
    request<{ deleted: boolean }>(`/admin/shops/${shop}/modifier-groups/${groupId}`, {
      method: 'DELETE',
    }),

  /** One tap from a phone mid-service: "no oat milk today". */
  setOptionAvailability: (shop: string, optionId: string, isAvailable: boolean) =>
    request<{ updated: boolean }>(
      `/admin/shops/${shop}/modifier-options/${optionId}/availability`,
      { method: 'POST', body: JSON.stringify({ isAvailable }) },
    ),

  saveFnbItem: (shop: string, productId: string, body: UpsertFnbItemInput) =>
    request<{ productId: string }>(`/admin/shops/${shop}/products/${productId}/fnb`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  setSoldOut: (shop: string, productId: string, isSoldOut: boolean) =>
    request<{ updated: boolean }>(`/admin/shops/${shop}/products/${productId}/sold-out`, {
      method: 'POST',
      body: JSON.stringify({ isSoldOut }),
    }),

  soldOut: (shop: string) =>
    request<{ items: Array<{ productId: string; name: { km: string; en: string } }> }>(
      `/admin/shops/${shop}/sold-out`,
    ),

  /** Opening the shop for the day: everything back on the menu at once. */
  clearSoldOut: (shop: string) =>
    request<{ cleared: number }>(`/admin/shops/${shop}/sold-out/clear`, { method: 'POST' }),

  /* --- Services ----------------------------------------------------- */

  resources: (shop: string) =>
    request<{ resources: ResourceDto[] }>(`/admin/shops/${shop}/resources`),

  createResource: (shop: string, body: UpsertResourceInput) =>
    request<{ resource: ResourceDto }>(`/admin/shops/${shop}/resources`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateResource: (shop: string, resourceId: string, body: UpsertResourceInput) =>
    request<{ resource: ResourceDto }>(`/admin/shops/${shop}/resources/${resourceId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /**
   * Deactivate, never delete: the appointments this person already has must
   * keep the name that was promised to the customer.
   */
  deactivateResource: (shop: string, resourceId: string) =>
    request<{ deactivated: boolean }>(`/admin/shops/${shop}/resources/${resourceId}`, {
      method: 'DELETE',
    }),

  saveService: (shop: string, productId: string, body: UpsertServiceInput) =>
    request<{ productId: string }>(`/admin/shops/${shop}/products/${productId}/service`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  appointments: (shop: string, params: { from: string; to: string; resourceId?: string }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.resourceId) query.set('resourceId', params.resourceId);
    return request<{ appointments: AppointmentDto[] }>(
      `/admin/shops/${shop}/appointments?${query.toString()}`,
    );
  },

  /** A lunch break or a day off — the slot engine subtracts these like bookings. */
  blockTime: (shop: string, body: { resourceId: string; startsAt: string; endsAt: string; note?: string }) =>
    request<{ hold: { id: string } }>(`/admin/shops/${shop}/appointments/block`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  removeBlock: (shop: string, holdId: string) =>
    request<{ removed: boolean }>(`/admin/shops/${shop}/appointments/${holdId}`, {
      method: 'DELETE',
    }),

  rescheduleAppointment: (
    shop: string,
    body: { orderId: string; resourceId: string; startsAt: string },
  ) =>
    request<{ hold: unknown }>(`/admin/shops/${shop}/appointments/reschedule`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  adminSlots: (shop: string, params: { productId: string; date: string; resourceId?: string }) => {
    const query = new URLSearchParams({ productId: params.productId, date: params.date });
    if (params.resourceId) query.set('resourceId', params.resourceId);
    return request<SlotDayDto>(`/admin/shops/${shop}/slots?${query.toString()}`);
  },

  devSignIn: () => request<SessionPayload>('/auth/dev', { method: 'POST' }),

  signInWithWidget: (payload: Record<string, unknown>) =>
    request<SessionPayload>('/auth/telegram/widget', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  signInWithInitData: (initData: string) =>
    request<SessionPayload>('/auth/telegram/initdata', {
      method: 'POST',
      body: JSON.stringify({ initData }),
    }),

  me: () => request<{ user: SessionPayload['user']; memberships: SessionPayload['memberships'] }>('/auth/me'),

  createShop: (body: {
    name: string;
    currency: 'USD' | 'KHR';
    defaultLocale: 'km' | 'en';
    /** §4. Chosen at creation and fixed thereafter — see the picker's note. */
    vertical: 'retail' | 'fnb' | 'services' | 'digital';
  }) =>
    request<{ shop: { id: string; slug: string } }>('/admin/shops', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  shop: (id: string) => request<ShopPayload>(`/admin/shops/${id}`),

  updateShop: (id: string, patch: Record<string, unknown>) =>
    request<{ shop: unknown }>(`/admin/shops/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  dashboard: (id: string) => request<DashboardPayload>(`/admin/shops/${id}/dashboard`),

  orders: (id: string, params: { status?: string; q?: string; cursor?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.q) query.set('q', params.q);
    if (params.cursor) query.set('cursor', params.cursor);
    return request<{ items: OrderSummaryDto[]; nextCursor: string | null }>(
      `/admin/shops/${id}/orders?${query.toString()}`,
    );
  },

  order: (id: string, orderId: string) =>
    request<{
      order: OrderDto;
      payment: { id: string; status: string; qrPayload: string | null; amount: { amount: number; currency: 'USD' | 'KHR' } } | null;
    }>(`/admin/shops/${id}/orders/${orderId}`),

  orderAction: (id: string, orderId: string, action: OrderAction) =>
    request<{ status: string }>(`/admin/shops/${id}/orders/${orderId}/actions`, {
      method: 'POST',
      body: JSON.stringify(action),
    }),

  products: (id: string, params: { q?: string; cursor?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.cursor) query.set('cursor', params.cursor);
    return request<{ items: ProductDto[]; nextCursor: string | null }>(
      `/admin/shops/${id}/products?${query.toString()}`,
    );
  },

  product: (id: string, productId: string) =>
    request<{ product: ProductDto }>(`/admin/shops/${id}/products/${productId}`),

  createProduct: (id: string, body: UpsertProductInput) =>
    request<{ product: ProductDto }>(`/admin/shops/${id}/products`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateProduct: (id: string, productId: string, body: UpsertProductInput) =>
    request<{ product: ProductDto }>(`/admin/shops/${id}/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  variantMatrix: (
    id: string,
    body: { sizes: string[]; colors: Array<{ name: string; hex?: string | null }>; defaultStock: number; skuPrefix?: string },
  ) =>
    request<{
      variants: Array<{ size: string | null; color: string | null; colorHex: string | null; sku: string; stock: number }>;
    }>(`/admin/shops/${id}/products/variant-matrix`, { method: 'POST', body: JSON.stringify(body) }),

  adjustStock: (id: string, adjustments: Array<{ variantId: string; stock: number }>) =>
    request<{ adjusted: Array<{ variantId: string; stock: number }> }>(`/admin/shops/${id}/stock`, {
      method: 'POST',
      body: JSON.stringify({ adjustments }),
    }),

  shipping: (id: string) =>
    request<{
      currency: 'USD' | 'KHR';
      rates: Array<{ id: string; provinceCode: string; price: number; isActive: boolean; freeAboveAmount: number | null }>;
    }>(`/admin/shops/${id}/shipping`),

  saveShipping: (
    id: string,
    rates: Array<{ provinceCode: string; priceAmount: number; isActive: boolean; freeAboveAmount?: number | null }>,
  ) => request<{ rates: unknown[] }>(`/admin/shops/${id}/shipping`, { method: 'PUT', body: JSON.stringify({ rates }) }),

  providers: (id: string) =>
    request<{ providers: Array<{ provider: string; enabled: boolean; health: string; message: string | null }> }>(
      `/admin/shops/${id}/payments/providers`,
    ),

  unmatchedPayments: (id: string) =>
    request<{ notifications: Array<{ id: string; amount: number | null; currency: string | null; payerName: string | null; rawText: string }> }>(
      `/admin/shops/${id}/payments/unmatched`,
    ),

  forwardNotification: (id: string, text: string) =>
    request<{ kind: string; orderId?: string; reason?: string }>(`/admin/shops/${id}/payments/forward`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  attachNotification: (id: string, notificationId: string, orderId: string) =>
    request<{ confirmed: boolean }>(`/admin/shops/${id}/payments/attach`, {
      method: 'POST',
      body: JSON.stringify({ notificationId, orderId }),
    }),

  /* §4 `digital`: the code list IS the stock. */

  digitalStock: (id: string) =>
    request<{
      items: Array<{
        productId: string;
        name: { km: string; en: string };
        status: string;
        available: number;
        undelivered: number;
        sold: number;
        lowStockThreshold: number;
        instructions: string | null;
        low: boolean;
      }>;
    }>(`/admin/shops/${id}/digital/stock`),

  addDigitalCodes: (id: string, productId: string, codes: string[]) =>
    request<{ added: number }>(`/admin/shops/${id}/digital/codes`, {
      method: 'POST',
      body: JSON.stringify({ productId, codes }),
    }),

  saveDigitalItem: (
    id: string,
    body: { productId: string; instructions?: string | null; lowStockThreshold?: number },
  ) =>
    request<{ item: unknown }>(`/admin/shops/${id}/digital/item`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  resendDigitalCodes: (id: string, orderId: string) =>
    request<{ sent: number }>(`/admin/shops/${id}/digital/orders/${orderId}/resend`, {
      method: 'POST',
    }),

  members: (id: string) =>
    request<{
      members: Array<{
        userId: string;
        name?: string;
        firstName: string;
        lastName: string | null;
        username: string | null;
        role: 'owner' | 'staff';
        status: string;
      }>;
    }>(`/admin/shops/${id}/members`),

  inviteStaff: (id: string, role: 'owner' | 'staff') =>
    request<{ link: string }>(`/admin/shops/${id}/members/invites`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    }),

  /**
   * Upgrading opens a CHARGE and returns the QR that settles it (§3).
   *
   * It deliberately does not change the plan — that happens when the money
   * lands, either matched automatically against an ABA notification or
   * confirmed by a human.
   */
  startPlanCharge: (plan: 'starter' | 'pro' | 'business') =>
    request<{ charge: PlatformCharge }>(`/admin/account/billing/plan`, {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),

  startCreditCharge: (packId: string) =>
    request<{ charge: PlatformCharge }>(`/admin/account/billing/credits`, {
      method: 'POST',
      body: JSON.stringify({ packId }),
    }),

  openCharges: () => request<{ charges: PlatformCharge[] }>(`/admin/account/billing/charges`),

  /**
   * The web half of the staff invite (§3 `identity-team`).
   *
   * Only reached when the shop has no bot yet — otherwise the owner shares a
   * `t.me` deep link and the bot does this instead.
   */
  acceptInvite: (token: string) =>
    request<{ shopId: string; role: 'owner' | 'staff' }>(`/auth/invites/accept`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /**
   * §3 `bot-provisioning`: the links that start bot setup.
   *
   * `createLink` is Telegram's own create-a-bot screen (Bot API 10.0) — the
   * merchant never handles a token. `deepLink` opens our manager bot, which
   * is where the BotFather fallback lives.
   */
  provisionBot: (id: string) =>
    request<{
      bot: { state: string; username: string | null };
      deepLink: string;
      createLink: string;
    }>(`/admin/shops/${id}/bot/provision`, { method: 'POST' }),

  attachBot: (id: string, botToken: string) =>
    request<{ bot: { username: string | null }; manualStepsRequired: string[] }>(
      `/admin/shops/${id}/bot/attach`,
      { method: 'POST', body: JSON.stringify({ token: botToken }) },
    ),

  setAbaGroup: (id: string, chatId: number, title?: string) =>
    request<{ listenerState: string; isAdmin: boolean; fallbackMessage: string | null }>(
      `/admin/shops/${id}/bot/aba-group`,
      { method: 'POST', body: JSON.stringify({ chatId, ...(title ? { title } : {}) }) },
    ),

  botHealth: (id: string) =>
    request<{ ok: boolean; issues: string[] }>(`/admin/shops/${id}/bot/health`),

  /**
   * No shop id: billing belongs to the account (§3). One plan, one balance,
   * one ledger, whichever shop the merchant happens to have open.
   */
  billing: () =>
    request<{
      balance: { plan: number; purchased: number; total: number; available: number };
      subscription: { plan: string; status: string; periodEnd: string | null } | null;
      ledger: Array<{ id: string; kind: string; amount: number; note: string | null; createdAt: string }>;
      shops: Array<{ id: string; name: string; slug: string; status: string }>;
    }>('/admin/account/billing'),

  facebookImport: (id: string, body: { url: string; caption?: string; imageUrls?: string[] }) =>
    request<{ jobId: string; credits: number }>(`/admin/shops/${id}/ai/facebook-import`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  aiJob: (id: string, jobId: string) =>
    request<{ job: { id: string; status: string; output: unknown; error: string | null } }>(
      `/admin/shops/${id}/ai/jobs/${jobId}`,
    ),

  /* ---------------------------------------------------------------- */
  /* v2 growth                                                         */
  /* ---------------------------------------------------------------- */

  promotions: (id: string) =>
    request<{ promotions: Array<PromotionDto & { shareUrl: string | null }> }>(
      `/admin/shops/${id}/promotions`,
    ),

  createPromotion: (id: string, body: UpsertPromotionInput) =>
    request<{ promotion: unknown }>(`/admin/shops/${id}/promotions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updatePromotion: (id: string, promotionId: string, body: UpsertPromotionInput) =>
    request<{ promotion: unknown }>(`/admin/shops/${id}/promotions/${promotionId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deletePromotion: (id: string, promotionId: string) =>
    request<{ deactivated: boolean }>(`/admin/shops/${id}/promotions/${promotionId}`, {
      method: 'DELETE',
    }),

  affiliates: (id: string) =>
    request<{ affiliates: AffiliateDto[] }>(`/admin/shops/${id}/affiliates`),

  createAffiliate: (id: string, body: UpsertAffiliateInput) =>
    request<{ affiliate: unknown }>(`/admin/shops/${id}/affiliates`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateAffiliate: (id: string, affiliateId: string, body: UpsertAffiliateInput) =>
    request<{ affiliate: unknown }>(`/admin/shops/${id}/affiliates/${affiliateId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  commissions: (id: string, params: { status?: string; affiliateId?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.affiliateId) query.set('affiliateId', params.affiliateId);
    return request<{ commissions: CommissionDto[] }>(
      `/admin/shops/${id}/commissions?${query.toString()}`,
    );
  },

  payCommissions: (
    id: string,
    body: { affiliateId: string; commissionIds: string[]; reference: string },
  ) =>
    request<{ paid: number; amount: number }>(`/admin/shops/${id}/commissions/pay`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  broadcasts: (id: string) => request<{ broadcasts: BroadcastDto[] }>(`/admin/shops/${id}/broadcasts`),

  previewSegment: (id: string, segment: BroadcastSegment) =>
    request<{ recipients: number }>(`/admin/shops/${id}/broadcasts/preview?segment=${segment}`),

  createBroadcast: (id: string, body: UpsertBroadcastInput) =>
    request<{ broadcast: { id: string } }>(`/admin/shops/${id}/broadcasts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  sendBroadcast: (id: string, broadcastId: string) =>
    request<{ queued: number }>(`/admin/shops/${id}/broadcasts/${broadcastId}/send`, {
      method: 'POST',
    }),

  cancelBroadcast: (id: string, broadcastId: string) =>
    request<{ cancelled: boolean }>(`/admin/shops/${id}/broadcasts/${broadcastId}/cancel`, {
      method: 'POST',
    }),

  assistant: (id: string) =>
    request<{
      status: {
        connected: boolean;
        canReply: boolean;
        mode: 'draft' | 'auto';
        isEnabled: boolean;
        connectedAt?: string;
      };
      botUsername: string | null;
    }>(`/admin/shops/${id}/assistant`),

  setAssistantMode: (id: string, mode: 'draft' | 'auto') =>
    request<{ status: { mode: 'draft' | 'auto' } }>(`/admin/shops/${id}/assistant`, {
      method: 'PUT',
      body: JSON.stringify({ mode }),
    }),

  automation: (id: string) =>
    request<{ settings: AutomationSettingsInput }>(`/admin/shops/${id}/automation`),

  saveAutomation: (id: string, patch: Partial<AutomationSettingsInput>) =>
    request<{ settings: AutomationSettingsInput }>(`/admin/shops/${id}/automation`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  analytics: (id: string, params: { from?: string; to?: string; granularity?: 'day' | 'week' } = {}) => {
    const query = new URLSearchParams();
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    query.set('granularity', params.granularity ?? 'day');
    return request<AnalyticsDto>(`/admin/shops/${id}/analytics?${query.toString()}`);
  },

  /* ---------------------------------------------------------------- */
  /* Trust and safety (§6.7)                                           */
  /* ---------------------------------------------------------------- */

  /** §8's onboarding checklist, computed server-side from real shop state. */
  onboarding: (id: string) =>
    request<{
      steps: Array<{ id: OnboardingStepId; done: boolean; required: boolean }>;
      complete: boolean;
      next: OnboardingStepId | null;
      storefrontUrl: string;
    }>(`/admin/shops/${id}/onboarding`),

  policy: (id: string) =>
    request<{
      status: PolicyStatusDto;
      prohibited: ProhibitedCategory[];
      returnPolicyTemplates: ReturnPolicyTemplate[];
      taxNotice: { title: { km: string; en: string }; body: { km: string; en: string }; linkLabel: { km: string; en: string }; url: string };
      shop: { status: string; suspendedReason: string | null; suspendedAt: string | null };
    }>(`/admin/shops/${id}/policy`),

  acceptPolicy: (id: string, documents: string[]) =>
    request<{ status: PolicyStatusDto }>(`/admin/shops/${id}/policy/accept`, {
      method: 'POST',
      body: JSON.stringify({ documents }),
    }),

  adoptReturnTemplate: (id: string, templateId: string) =>
    request<{ returnPolicy: { km: string; en: string } }>(
      `/admin/shops/${id}/policy/return-template`,
      { method: 'POST', body: JSON.stringify({ templateId }) },
    ),

  shopReports: (id: string) =>
    request<{
      reports: Array<{
        id: string;
        reason: string;
        detail: string;
        status: string;
        resolution: string | null;
        createdAt: string;
      }>;
      history: Array<{ action: string; reason: string; createdAt: string }>;
    }>(`/admin/shops/${id}/reports`),

  exportOrdersUrl: (id: string) => `${BASE}/admin/shops/${id}/export/orders.csv`,

  stream: (id: string) => new EventSource(`${BASE}/admin/shops/${id}/stream`),
};
